import api from '@/store/api';
import { athena, boro } from '@/store/api/instances';

interface GetServiceTicketNoteAttachmentRequest {
  service_ticket_id: string;
  note_id: string;
  attachment_id: string;
}

interface GetServiceTicketNoteAttachmentResponse {
  id: string;
  note_id: string;
  file_name: string;
  file_url: string;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

interface CreateServiceTicketNoteRequest {
  service_ticket_id: string;
  note_text: string;
  author: string;
}

export interface ServiceTicketNoteAttachment {
  id: string;
  note_id: string;
  file_name: string;
  file_url: string;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

interface CreateServiceTicketNoteResponse {
  id: string;
  service_ticket_id: string;
  body: string;
  author: string;
  type: string;
  created_by: string;
  created_at: string;
  last_updated_by: string;
  last_updated_at: string;
  attachments: ServiceTicketNoteAttachment[];
}

interface UploadServiceTicketNoteAttachmentRequest {
  service_ticket_id: string;
  service_ticket_note_id: string;
  files: File[];
}

interface UploadServiceTicketNoteAttachmentResponse {
  id: string;
  service_ticket_note_id: string;
  file_name: string;
  file_url: string;
  uploaded_by: string;
  uploaded_at: string;
}

/**
 * RTK Query API for Service Ticket Notes.
 */
export const serviceTicketNotesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getServiceTicketNoteAttachment: build.query<
      GetServiceTicketNoteAttachmentResponse,
      GetServiceTicketNoteAttachmentRequest
    >({
      query: ({ service_ticket_id, note_id, attachment_id }) =>
        athena({
          url: `/v2/Operations/service-tickets/${service_ticket_id}/notes/${note_id}/attachments/${attachment_id}`,
          method: 'GET',
        }),
    }),
    createServiceTicketNote: build.mutation<
      CreateServiceTicketNoteResponse,
      CreateServiceTicketNoteRequest
    >({
      query: ({ service_ticket_id, ...data }) =>
        boro({
          url: `/v2/Operations/service-tickets/${service_ticket_id}/notes`,
          method: 'POST',
          data,
        }),
      invalidatesTags: (_, __, { service_ticket_id }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: service_ticket_id },
        { type: 'ServiceTicketNotes', id: service_ticket_id },
      ],
    }),
    uploadServiceTicketNoteAttachment: build.mutation<
      UploadServiceTicketNoteAttachmentResponse,
      UploadServiceTicketNoteAttachmentRequest
    >({
      query: ({ service_ticket_id, service_ticket_note_id, files }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });

        return boro({
          url: `/v2/Operations/service-tickets/${service_ticket_id}/notes/${service_ticket_note_id}/attachments`,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },
      invalidatesTags: (_, __, { service_ticket_id }) => [
        'ServiceTickets',
        { type: 'ServiceTickets', id: service_ticket_id },
        { type: 'ServiceTicketNotes', id: service_ticket_id },
      ],
    }),
  }),
});

export const {
  useGetServiceTicketNoteAttachmentQuery,
  useLazyGetServiceTicketNoteAttachmentQuery,
  useCreateServiceTicketNoteMutation,
  useUploadServiceTicketNoteAttachmentMutation,
} = serviceTicketNotesApi;

export const {
  endpoints: {
    getServiceTicketNoteAttachment,
    createServiceTicketNote,
    uploadServiceTicketNoteAttachment,
  },
} = serviceTicketNotesApi;
